// Events
import { INIT_MOVEABLE_CONTENT } from 'events';
import PhillRecommendations from 'lib/PhillRecommendations';
// Lib
import Tracking from 'lib/tracking';
import { component } from 'picoapp';

// Utils
import choozy from 'utils/choozy';
import { searchProductItem } from 'utils/product-item';
import getString from 'utils/string';
import waitToLoad from 'utils/waitToLoad';

export default component(async (node, ctx) => {
  const {
    searchClearFilterButton,
    searchTitleTerms,
    searchStats,
    searchFilterModal,
    searchSelectedFilters,
    searchFilter: searchFilters = [],
    searchPageResults,
    searchResultsShowMoreBtn,
    searchPageSuggestions,
    searchAutocompleteForm,
    searchPageRecommendations,
  } = choozy(node);
  const { searchPageInput: searchPageInputs, searchPageClearButton: searchPageClearButtons } =
    choozy(document.body, null);
  const { paginationAmount } = node.dataset;
  const {
    appId,
    apiKey,
    indices: { productsIndex: indexName },
    algoliaQuerySuggestionsIndex,
  } = window.theme.algoliaSettings;
  const { product, products } = window.theme.strings.search;

  let algoliasearch;
  let instantsearch;
  let createQuerySuggestionsPlugin;
  let autocomplete;
  let connectSearchBox;
  let connectInfiniteHits;
  let connectClearRefinements;
  let connectCurrentRefinements;
  let configure;
  let stats;
  let refinementList;

  try {
    await waitToLoad('AlgoliaSearch');

    algoliasearch = window.algoliasearch;
    instantsearch = window.instantsearch;
    createQuerySuggestionsPlugin =
      window['@algolia/autocomplete-plugin-query-suggestions'].createQuerySuggestionsPlugin;
    autocomplete = window['@algolia/autocomplete-js'].autocomplete;
    connectSearchBox = instantsearch.connectors.connectSearchBox;
    connectInfiniteHits = instantsearch.connectors.connectInfiniteHits;
    connectClearRefinements = instantsearch.connectors.connectClearRefinements;
    connectCurrentRefinements = instantsearch.connectors.connectCurrentRefinements;
    configure = instantsearch.widgets.configure;
    stats = instantsearch.widgets.stats;
    refinementList = instantsearch.widgets.refinementList;
  } catch (err) {
    // eslint-disable-next-line
    console.error(err);

    return;
  }

  const emptyTermsAttribute = 'open';
  const noResultsAttribute = 'active';
  const initialTerms =
    searchPageInputs[0].value ||
    new URLSearchParams(decodeURI(window.location.search)).get(`${indexName}[query]`) ||
    '';
  const filterAttributes = {
    type: 'product_type',
    gender: 'named_tags.gender',
    color: 'meta.data.colors',
    fit: 'named_tags.fit',
    size: 'options.size',
    waist: 'options.waist',
    length: 'options.length',
  };
  const suggestionsSelector = '[data-autocomplete-source-id="querySuggestionsPlugin"]';
  const { Algolia: allowed } = window.theme.consent || { Algolia: false };
  const searchClient = algoliasearch(appId, apiKey);

  const search = instantsearch({
    indexName,
    searchClient,
    routing: true,
    insights: allowed,
    insightsInitParams: {
      useCookie: false,
    },
    initialUiState: {
      [indexName]: {
        query: initialTerms,
        page: 1,
      },
    },
  });
  let currentHits = [];
  let suggestionsAndRecommendationsInited = false;

  const initSuggestionsAndRecommendations = () => {
    if (suggestionsAndRecommendationsInited) return;

    const querySuggestionsPlugin = createQuerySuggestionsPlugin({
      searchClient,
      indexName: algoliaQuerySuggestionsIndex,
      transformSource({ source }) {
        return {
          ...source,
          getItemUrl({ item }) {
            return `/search?q=${item.query}`;
          },
          templates: {
            item(params) {
              const { item, html } = params;

              return html`<div
                data-query-suggestion
                class="mb-2 w-max max-w-full cursor-pointer text-xl leading-6 text-black"
              >
                ${item.query}
              </div> `;
            },
          },
        };
      },
    });

    const algoliaAutocomplete = autocomplete({
      container: '[data-search-autocomplete-form]',
      plugins: [querySuggestionsPlugin],
      openOnFocus: true,
      detachedMediaQuery: 'none',
      initialState: {
        query: '',
      },
    });

    const suggestionsObserver = new MutationObserver(([entry], observer) => {
      entry.addedNodes.forEach((addedNode) => {
        if (addedNode.nodeType === Node.ELEMENT_NODE && addedNode?.closest('.aa-DetachedOverlay')) {
          const suggestionsList = addedNode.matches(suggestionsSelector)
            ? addedNode
            : addedNode.querySelector(suggestionsSelector);

          if (suggestionsList) {
            searchPageSuggestions.innerHTML = addedNode.innerHTML;
            algoliaAutocomplete.destroy();
            searchAutocompleteForm.remove();
            observer.disconnect();
          }
        }
      });
    });

    suggestionsObserver.observe(document.body, { childList: true, subtree: true });
    searchAutocompleteForm.querySelector('button')?.click();

    const recommendationsEngine = new PhillRecommendations({
      appId,
      apiKey,
      index: indexName,
      retrievableAttributes: [
        'title',
        'handle',
        'product_image',
        'product_type',
        'price',
        'compare_at_price',
      ],
    });

    recommendationsEngine
      .query(['trending-items'])
      .then(([hits]) => {
        searchPageRecommendations.innerHTML = `${hits
          .map(
            (hit, index) =>
              `${searchProductItem({
                ...hit,
                url: `/products/${hit.handle}`,
                image: {
                  url: hit.product_image,
                  width: 750,
                  height: 1000,
                },
                type: hit.product_type,
                price: hit.price * 100,
                compare_at_price_max: hit.compare_at_price * 100,
                index,
              })}`,
          )
          .join('')}
          `;

        Tracking.trackViewItemList({
          products: hits.map((item, index) => ({
            ...item,
            index,
            price: item.price * 100,
          })),
          placement: 'search_page_recommendations',
        });
      })
      .catch((e) => {
        // eslint-disable-next-line
        console.error(e);
      });

    suggestionsAndRecommendationsInited = true;
  };

  const updateSectionState = (searchQuery = null, searchResultsLength = null) => {
    if (searchResultsLength === null) {
      node[searchQuery ? 'removeAttribute' : 'setAttribute'](emptyTermsAttribute, '');
      node[searchQuery ? 'removeAttribute' : 'setAttribute'](noResultsAttribute, '');
    } else {
      node[searchQuery && !searchResultsLength ? 'setAttribute' : 'removeAttribute'](
        noResultsAttribute,
        '',
      );
    }

    if ((searchQuery && searchResultsLength === 0) || initialTerms === '')
      initSuggestionsAndRecommendations();
  };

  const updateDocumentTitle = (query) => {
    document.title = getString('search.documentTitle', { query });
  };

  updateDocumentTitle(initialTerms);

  // Search

  const renderSearchBox = (renderOptions, isFirstRender) => {
    const { query, widgetParams, refine } = renderOptions;
    const { input, clearButton } = widgetParams;

    if (isFirstRender) {
      clearButton.addEventListener('click', () => {
        searchPageInputs.forEach((searchPageInput) => {
          searchPageInput.value = '';
        });
      });

      input.addEventListener('input', (e) => {
        if (searchClearFilterButton && searchClearFilterButton.classList.contains('open')) {
          searchClearFilterButton.click();
        }

        if (e.target.value !== '') refine(e.target.value);
      });

      searchPageSuggestions.addEventListener('click', (event) => {
        const { target } = event;

        if (target.matches('[data-query-suggestion]')) refine(target.innerText);
      });

      updateSectionState(query);
      node.classList.add('show');
    }

    if (input.value !== query) {
      updateDocumentTitle(query);
      updateSectionState(query);
      input.value = query;
      searchTitleTerms.forEach((element) => {
        element.innerText = query;
      });
      clearButton[query ? 'removeAttribute' : 'setAttribute']('hidden', '');
    }
  };

  const renderHits = (renderOptions, isFirstRender) => {
    const { results, hits, widgetParams, showMore, isLastPage, sendEvent, currentPageHits } =
      renderOptions;

    if (isFirstRender) {
      searchResultsShowMoreBtn.addEventListener('click', () => {
        showMore();
      });

      widgetParams.container.addEventListener('click', (event) => {
        const currentItem = event.target.closest('[data-search-item-link]');
        const index = currentItem?.dataset.searchItemIndex;

        if (!currentItem) return;

        sendEvent('click', currentHits[+index], 'Search page: Clicked product result');
        localStorage.setItem(
          'algolia_recent_product',
          JSON.stringify({
            // eslint-disable-next-line no-underscore-dangle
            queryID: currentHits[+index].__queryID,
            id: currentHits[+index].id,
          }),
        );
      });

      widgetParams.container.addEventListener('click', (event) =>
        Tracking.selectItemCallback(event, false, true),
      );
    }

    if (
      hits &&
      JSON.stringify(hits?.map((hit) => hit.objectID)) !==
        JSON.stringify(currentHits?.map((hit) => hit.objectID))
    )
      Tracking.trackViewItemList({
        products: currentPageHits?.map((item, index) => ({
          ...item,
          index,
          price: item.price * 100,
        })),
        placement: 'search_page',
      });

    currentHits = hits;
    updateSectionState(results?.query, hits.length);
    searchResultsShowMoreBtn.classList[isLastPage || !results?.query ? 'remove' : 'add']('open');
    widgetParams.container.innerHTML = results?.query
      ? `${hits
          .map(
            (hit, index) =>
              `${searchProductItem(
                {
                  ...hit,
                  url: `/products/${hit.handle}`,
                  image: {
                    url: hit.product_image,
                    width: 750,
                    height: 1000,
                  },
                  type: hit.product_type,
                  price: hit.price * 100,
                  compare_at_price_max: hit.compare_at_price * 100,
                  index,
                },
                ['data-search-item-link', `data-search-item-index="${index}"`],
              )}`,
          )
          .join('')}
        `
      : '';
  };

  const renderClearRefinements = (renderOptions, isFirstRender) => {
    const { refine, widgetParams, canRefine } = renderOptions;
    const { button: clearButton } = widgetParams;

    if (isFirstRender) {
      clearButton.addEventListener('click', () => {
        refine();
      });
    }

    clearButton.classList[canRefine ? 'add' : 'remove']('open');
  };

  const renderCurrentRefinementsItems = (items) =>
    items
      .map((filterItem) =>
        filterItem.refinements
          .map((refinement) => {
            const label = window.theme.strings.search.filter_label[filterItem.label];
            const value =
              filterItem.label in window.theme.strings.search.filter_value
                ? window.theme.strings.search.filter_value[filterItem.label][
                    refinement.label.toLowerCase()
                  ] || refinement.label
                : refinement.label;

            return `
            <button class='bg-black rounded text-body_xxs font-normal text-white py-1.5 px-2 w-full flex items-center before:content-["\\2715"] before:font-bold before:block before:mr-3'
              ${Object.keys(refinement)
                .map((key) => `data-${key}="${refinement[key]}"`)
                .join(' ')}
            >
              <span class='line-clamp-1 capitalize pointer-events-none'>${label}: ${value} </span>
            </button>
          `;
          })
          .join(''),
      )
      .join('');

  const renderCurrentRefinements = (renderOptions, isFirstRender) => {
    const { items, refine, widgetParams } = renderOptions;

    if (isFirstRender) {
      widgetParams.container.addEventListener('click', (event) => {
        const { target } = event;

        if (target.tagName !== 'BUTTON') return;

        const refinement = Object.keys(target.dataset).reduce(
          (acc, key) => ({
            ...acc,
            [key]: target.dataset[key],
          }),
          {},
        );

        refine(refinement);
      });
    }

    widgetParams.container.innerHTML = renderCurrentRefinementsItems(items);
  };

  const customSearchBox = connectSearchBox(renderSearchBox);
  const customHits = connectInfiniteHits(renderHits);
  const customClearRefinements = connectClearRefinements(renderClearRefinements);
  const customCurrentRefinements = connectCurrentRefinements(renderCurrentRefinements);

  const createCheckboxItemRenderer =
    (filterAttribute) =>
    (filterItem, { html }) => {
      const { url, label, count, isRefined } = filterItem;

      const translatedLabel =
        filterAttribute in window.theme.strings.search.filter_value
          ? window.theme.strings.search.filter_value[filterAttribute][label.toLowerCase()] || label
          : label;

      return html`
        <a
          href="${url}"
          class="mt-2 items-center space-x-2 text-body_xxs font-normal text-black flex"
        >
          <div
            class="${isRefined
              ? 'open'
              : ''} h-4 w-4 justify-center rounded border border-grey-border flex after:mt-1 after:h-1 after:w-2 after:-rotate-45 after:border-black open:border-black open:after:border-b open:after:border-l open:after:content-[''] open:after:block"
          ></div>
          <span class="!mr-auto capitalize">${translatedLabel}</span>
          <span class="text-grey-text">${count}</span>
        </a>
      `;
    };

  const renderLinkItem = (filterItem, { html }) => {
    const { url, label, count, isRefined } = filterItem;

    return html`
      <a
        href="${url}"
        class="mt-1 items-center justify-between space-x-2 text-body_xxs font-normal text-black flex"
      >
        <span class="${isRefined ? 'font-medium' : ''}">${label}</span>
        <span class="text-grey-text">${count}</span>
      </a>
    `;
  };

  search.addWidgets([
    configure({
      hitsPerPage: +paginationAmount,
      clickAnalytics: true,
      filters: 'NOT meta.askphill.search_result_behaviour:hidden',
    }),
    ...[
      ...Array.from(searchPageInputs || []).map((el, index) =>
        customSearchBox({
          input: searchPageInputs[index],
          clearButton: searchPageClearButtons[index],
        }),
      ),
    ],
    stats({
      container: searchStats,
      templates: {
        text(data, { html }) {
          let count = '';

          if (data.hasManyResults) {
            count += `${data.nbHits} ${products}`;
          } else if (data.hasOneResult) {
            count += `1 ${product}`;
          } else {
            count += '';
          }

          return html`<span>${count}</span>`;
        },
      },
    }),
    customHits({
      container: searchPageResults,
    }),
    customCurrentRefinements({
      container: searchSelectedFilters,
    }),
    customClearRefinements({
      button: searchClearFilterButton,
    }),
    ...[
      ...Array.from(searchFilters).map((searchFilter) => {
        const { searchFilter: searchFilterName } = searchFilter.dataset;

        return refinementList({
          container: `[data-search-filter="${searchFilterName}"]`,
          attribute: filterAttributes[searchFilterName],
          templates: {
            item:
              searchFilterName === 'type'
                ? renderLinkItem
                : createCheckboxItemRenderer(filterAttributes[searchFilterName]),
          },
          sortBy: ['name'],
          cssClasses: {
            root: ['peer mb-7 empty:mb-0'],
            item: ['gender', 'fit'].includes(searchFilterName) ? 'capitalize' : '',
          },
        });
      }),
    ],
  ]);

  search.start();

  // Not sure what this is supposed to do
  searchFilterModal.addEventListener('click', (event) => {
    event.stopPropagation();
  });

  ctx.emit(INIT_MOVEABLE_CONTENT);
});
